import { useCallback, useEffect, useState } from 'react';

/** Script oficial de TikTok: convierte cada blockquote.tiktok-embed de la página en su recuadro (un iframe). */
const SCRIPT_TIKTOK = 'https://www.tiktok.com/embed.js';
/** Origen de los mensajes del iframe de TikTok. */
const ORIGEN_TIKTOK = /^https:\/\/([\w-]+\.)*tiktok\.com$/;
/** Un iframe vacío mide 150 px de alto (el alto por omisión); con el perfil adentro, el recuadro mide mucho más. */
const ALTO_VACIO = 200;
/** Tiempo que se espera, con la pestaña de TikTok abierta, antes de dar el recuadro por caído. */
const ESPERA_MS = 6000;

/**
 * Estado del recuadro de TikTok: todavía no responde, ya muestra el perfil o no respondió a tiempo.
 * @typedef {'cargando'|'listo'|'fallo'} EstadoTikTok
 */

/**
 * Recuadro de TikTok visto desde la sección de redes.
 * @typedef {Object} RecuadroTikTok
 * @property {EstadoTikTok} estado
 * @property {() => void} reintentar  vuelve a pedir el recuadro a TikTok
 */

function quitarScript() {
  document.querySelectorAll(`script[src="${SCRIPT_TIKTOK}"]`).forEach((s) => s.remove());
}

/** Pone el script de TikTok en la página. Si ya estaba, lo cambia por uno nuevo: el script solo la revisa al cargar. */
function ponerScript() {
  quitarScript();
  const s = document.createElement('script');
  s.src = SCRIPT_TIKTOK;
  s.async = true;
  document.body.appendChild(s);
}

/**
 * Carga y sigue el recuadro oficial del perfil de TikTok que va dentro de `contenedor`.
 * - Pone el script de TikTok cuando `pedido` pasa a true y lo quita al desmontar; así, al volver a Inicio desde otra
 *   página, el script nuevo arma otra vez el recuadro.
 * - 'listo': el iframe de TikTok ya le habla a la página (así le pasa su alto) o ya creció con el perfil.
 * - 'fallo': con la pestaña abierta (`visible`) pasaron 6 s sin eso. Pasa cuando TikTok está saturado (su iframe solo
 *   dice "overload-protect triggered") o cuando un bloqueador impide su script. Si después responde, vuelve a 'listo'.
 * @param {import('react').RefObject<HTMLElement>} contenedor  elemento donde va el blockquote de TikTok
 * @param {boolean} pedido   el blockquote ya está en la página
 * @param {boolean} visible  la pestaña de TikTok está abierta
 * @returns {RecuadroTikTok}
 */
export default function useRecuadroTikTok(contenedor, pedido, visible) {
  const [estado, setEstado] = useState(/** @type {EstadoTikTok} */ ('cargando'));

  useEffect(() => {
    const caja = contenedor.current;
    if (!pedido || !caja) return undefined;
    /** @returns {HTMLIFrameElement|null} */
    const iframe = () => caja.querySelector('iframe');
    /** @param {MessageEvent} e */
    const alMensaje = (e) => { if (ORIGEN_TIKTOK.test(e.origin) && iframe()) setEstado('listo'); };
    const medir = () => {
      const f = iframe();
      if (f && f.getBoundingClientRect().height > ALTO_VACIO) setEstado('listo');
    };
    const alto = typeof ResizeObserver === 'function' ? new ResizeObserver(medir) : null;
    // el script de TikTok cambia el contenido del blockquote por su iframe: desde ahí se vigila su alto
    const cambios = new MutationObserver(() => {
      const f = iframe();
      if (f && alto) alto.observe(f);
      medir();
    });
    window.addEventListener('message', alMensaje);
    cambios.observe(caja, { childList: true, subtree: true });
    ponerScript();
    return () => {
      window.removeEventListener('message', alMensaje);
      cambios.disconnect();
      if (alto) alto.disconnect();
      quitarScript();
    };
  }, [contenedor, pedido]);

  // el tiempo solo corre con la pestaña abierta: con ella cerrada, el navegador puede pausar el iframe escondido
  useEffect(() => {
    if (!pedido || !visible || estado !== 'cargando') return undefined;
    const reloj = setTimeout(() => setEstado('fallo'), ESPERA_MS);
    return () => clearTimeout(reloj);
  }, [pedido, visible, estado]);

  const reintentar = useCallback(() => {
    const f = contenedor.current && contenedor.current.querySelector('iframe');
    setEstado('cargando');
    // con iframe, se vuelve a pedir la página de TikTok; sin él (el script no cargó), se vuelve a poner el script
    if (f) f.setAttribute('src', f.getAttribute('src') || '');
    else ponerScript();
  }, [contenedor]);

  return { estado, reintentar };
}
